import elementService from "@/common/ElementService";

export default new class DetailTabSwitchService {
    private static readonly VARIANTS_TAB_ID = "variants";
    private static readonly VARIANTS_NEW_TAB_ID = "variants-new";
    private static readonly QUESTION_TAB_ID = "question";

    private readonly paneTabButtons: Map<string, HTMLButtonElement> = new Map<string, HTMLButtonElement>();
    private readonly panes: Map<string, HTMLDivElement> = new Map<string, HTMLDivElement>();

    constructor() {
        for (const paneTabSwitch of document.querySelectorAll<HTMLButtonElement>(".js-tab-pane-switch")) {
            this.paneTabButtons.set(this.getElementPaneId(paneTabSwitch), paneTabSwitch);
        }

        for (const pane of document.querySelectorAll<HTMLDivElement>(".js-tab-pane")) {
            this.panes.set(this.getElementPaneId(pane), pane);
        }
    }

    public init(): void {
        this.initPanesSwitch();
        this.initPanesToggle();
        this.initScrollToVariantButtons();
    }

    public switchAndScrollToVariantsTab(): void {
        const paneId = this.paneTabButtons.has(DetailTabSwitchService.VARIANTS_NEW_TAB_ID)
            ? DetailTabSwitchService.VARIANTS_NEW_TAB_ID
            : DetailTabSwitchService.VARIANTS_TAB_ID;
        this.switchToPane(paneId);
        this.scrollToPane(paneId);
    }

    public showQuestionForm(): void {
        this.switchToPane(DetailTabSwitchService.QUESTION_TAB_ID);
        this.scrollToPane(DetailTabSwitchService.QUESTION_TAB_ID);
    }

    private initPanesSwitch(): void {
        for (const paneSwitch of this.paneTabButtons.values()) {
            paneSwitch.addEventListener("click", () => this.switchTab(paneSwitch));
        }
    }

    private switchTab(paneSwitch: HTMLButtonElement): void {
        this.switchToPane(this.getElementPaneId(paneSwitch));
    }

    private initPanesToggle(): void {
        for (const paneToggle of document.querySelectorAll<HTMLButtonElement>(".js-tab-pane-toggle")) {
            paneToggle.addEventListener("click", () => this.togglePane(paneToggle));
        }
    }

    private initScrollToVariantButtons(): void {
        for (const scrollToVariantButton of document.querySelectorAll<HTMLElement>(".js-scroll-to-variant")) {
            scrollToVariantButton.addEventListener("click", (event: MouseEvent) => {
                event.preventDefault();
                const paneId = scrollToVariantButton.dataset.forcePane ?? (this.paneTabButtons.has(DetailTabSwitchService.VARIANTS_NEW_TAB_ID)
                    ? DetailTabSwitchService.VARIANTS_NEW_TAB_ID
                    : DetailTabSwitchService.VARIANTS_TAB_ID);
                this.switchToPane(paneId);
                this.scrollToPane(paneId);
            });
        }
    }

    private togglePane(paneToggle: HTMLButtonElement): void {
        const tabPane = elementService.closest<HTMLDivElement>(paneToggle, ".js-tab-pane");
        const tabPaneContent = elementService.elementQuerySelector<HTMLDivElement>(tabPane, ".js-tab-pane-content");

        if (tabPaneContent.classList.contains("active")) {
            paneToggle.classList.remove("active");
            tabPaneContent.classList.remove("active");
            tabPaneContent.classList.add("hidden");
        } else {
            paneToggle.classList.add("active");
            tabPaneContent.classList.add("active");
            tabPaneContent.classList.remove("hidden");
        }
    }

    private switchToPane(newPaneId: string): void {
        for (const currentPaneId of this.panes.keys()) {
            if (currentPaneId !== newPaneId) {
                this.closePane(currentPaneId);
            }
        }

        this.openPane(newPaneId);
        this.openPaneMobile(newPaneId);
    }

    private openPaneMobile(paneId: string): void {
        const tabPane = this.getPane(paneId);
        const paneToggle = elementService.elementQuerySelector<HTMLButtonElement>(tabPane, ".js-tab-pane-toggle");
        const tabPaneContent = elementService.elementQuerySelector<HTMLDivElement>(tabPane, ".js-tab-pane-content");

        paneToggle.classList.add("active");
        tabPaneContent.classList.add("active");
        tabPaneContent.classList.remove("hidden");
    }

    private getElementPaneId(paneElement: HTMLElement): string {
        return elementService.elementData(paneElement, "paneId");
    }

    private openPane(paneId: string): void {
        this.showPane(paneId);
        this.activatePaneSwitch(paneId);
    }

    private showPane(paneId: string): void {
        const pane = this.getPane(paneId);

        if (!pane.classList.contains("active")) {
            const tabPaneContent = elementService.elementQuerySelector<HTMLDivElement>(this.getPane(paneId), ".js-tab-pane-content");

            tabPaneContent.classList.remove("hidden");
            pane.classList.add("active");
            pane.classList.remove("hidden");
        }
    }

    private activatePaneSwitch(paneId: string): void {
        this.getPaneTabLI(paneId).classList.add("active");
    }

    private closePane(paneId: string): void {
        this.hidePane(paneId);
        this.deactivatePaneSwitch(paneId);
    }

    private hidePane(paneId: string): void {
        const pane = this.getPane(paneId);

        if (pane.classList.contains("active")) {
            pane.classList.remove("active");
        }
    }

    private deactivatePaneSwitch(paneId: string): void {
        this.getPaneTabLI(paneId).classList.remove("active");
    }

    private getPane(paneId: string): HTMLDivElement {
        const pane = this.panes.get(paneId);

        if (pane === undefined) {
            throw new Error(`Pane for tab ${paneId} not found.`);
        }

        return pane;
    }

    private getPaneTabLI(paneId: string): HTMLLIElement {
        const paneTabButton = this.paneTabButtons.get(paneId);

        if (paneTabButton === undefined) {
            throw new Error("Pane tab button not found.");
        }

        return elementService.closest<HTMLLIElement>(paneTabButton, "li");
    }

    private scrollToPane(paneId: string): void {
        const productTabs = elementService.querySelector<HTMLDivElement>(".js-product-tabs");

        if (productTabs.offsetParent !== null) {
            this.scrollToElement(productTabs);
        } else {
            this.scrollToElement(this.getPaneOrProductDetailCard(paneId));
        }
    }

    private getPaneOrProductDetailCard(paneId: string): HTMLDivElement {
        const pane = this.getPane(paneId);
        const productDetailCard = pane.querySelector<HTMLDivElement>(".js-product-detail-card");

        return productDetailCard ?? pane;
    }

    private scrollToElement(scrollElement: HTMLDivElement): void {
        const topPosition = scrollElement.getBoundingClientRect().top - document.body.getBoundingClientRect().top;

        window.scrollTo({
            top: topPosition,
            behavior: "smooth",
        });
    }
}();
